import { Routes, CanActivateFn } from '@angular/router';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppStateService, GlobalsService } from './core/services';

/**
 * Routes for the GUEST-ONLY build (`src/main.guest.ts` / `AppGuest`).
 *
 * Mirrors only the public "view shared draft" entry points from the main
 * `app.routes.ts`. Everything else (home, products, editor, cart, orders,
 * galleries, support, designer…) is intentionally absent so the bundle never
 * references those features.
 */

// Locale guard — keeps the active translate locale in sync with the URL
// segment. Unlike the main app's localeGuard it never redirects to a
// fallback route (there is no `login`/`home` here); an unrecognised locale
// just falls through and the bootstrap-set default locale stays in effect.
const guestLocaleGuard: CanActivateFn = (route) => {
  const appState = inject(AppStateService);
  const globals = inject(GlobalsService);
  const translate = inject(TranslateService);
  const locale = route.paramMap.get('locale');
  const supportedLocales = globals.languages.map(language => language.locale);

  if (locale && supportedLocales.includes(locale) && appState.locale() !== locale) {
    translate.use(locale);
    appState.setLocale(locale);
  }
  return true;
};

const loadGuestLogin = () =>
  import('./features/album-guest/album-guest-login.component')
    .then(m => m.AlbumGuestLoginComponent);

export const guestRoutes: Routes = [
  // Bare root-level share link: /share/:accessToken
  {
    path: 'share/:accessToken',
    loadComponent: loadGuestLogin
  },
  // Locale-prefixed forms: /:locale/share/:token and /:locale/album/preview/:token
  {
    path: ':locale/share/:accessToken',
    canActivate: [guestLocaleGuard],
    loadComponent: loadGuestLogin
  },
  {
    path: ':locale/album/preview/:accessToken',
    canActivate: [guestLocaleGuard],
    loadComponent: loadGuestLogin
  },
  // Anything else: neutral invalid/expired-link page (no login/home fallback).
  {
    path: '**',
    loadComponent: () => import('./features/album-guest/guest-invalid-link.component')
      .then(m => m.GuestInvalidLinkComponent)
  }
];
